// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.RelationshipActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
import { compose, withState, withHandlers } from 'recompose';
import CommonMessages from 'share/messages/Izenda.CommonMessages';
import { FIELDCHECK } from 'logic/data/Izenda.RelationshipConstants';
import { curry, isEmpty } from 'lodash/fp';

const actions = compose(
  withState('isLoading', 'updateLoading', true),
  withHandlers({
    onSave: curry(onSave),
    onAdd: curry(onAdd),
    onSearch: curry(onSearch),
    onCancel: curry(onCancel)
  })
);

function validateInvalidRelationships(store: any, validation: any): void {
  // Reset validation to remove current invalid required
  validation.reset();
  const allErrors = store.getInvalidErrors(FIELDCHECK);
  return validation.updateErrors(allErrors);
}

function onSave(props: any, placeholder: any): void {
  const { store, loader, localization, parentStore: dataModelStore, validation, isPopup } = props;
 
  const saveDone = function(result) {
    if (!result) {
      return null;
    }

    if (!result.success) {
      const requestErrors = store.processErrorAfterSave(result);
      !requestErrors[0].key && props.confirmMessage('error', requestErrors[0].messages);
    } else if (isPopup) {
      props.onClosePopup();
    } else {
      store.setShowInvalidScreen(false);
      store.setAlertMessage(
        CommonMessages.MESSAGE_TYPE.SUCCESS,
        localization.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED')
      );
      store.resetData();
      return store.getRelationships();
    }

    return result.messages;
  };

  const handleAfterValidate = function(result) {
    if (!result) {
      return null;
    }

    if (!result.success) {
      if (!store.showInvalidScreen) {
        store.setShowInvalidScreen(true);
      } else {
        store.getInvalidRelationships().then(function() {
          validateInvalidRelationships(store, validation);
        });
      }
      return null;
    } else {
      const message = result.hasPositionId
        ? localization.getText('RELATIONSHIP_MESSAGES_CONFIRM_SAVE_WITH_POSITION_ID')
        : localization.getText('RELATIONSHIP_MESSAGES_CONFIRM_SAVE_WITHOUT_POSITION_ID');
      return props
        .confirmFn('Confirmation', {
          description: message
        })
        .then(store.UpdateRelationshipToSolar.bind(store))
        .then(function(solarResponse) {
          if(solarResponse){
            return store.save();
          }
        });
  }};
 
  validation
    .validate()
    .then(function(success) {
      if (success && !store.hasChange) {
        if (isPopup) {
          props.onClosePopup();
        }
        store.alertMessage = {
          type: CommonMessages.MESSAGE_TYPE.INFO,
          message: localization.getText('COMMON_MESSAGES_NO_CHANGES_FOUND')
        };
      } else if (success) {
        loader.startLoading();
        // Do not check invalid or has Position ID in pop up display
        if (isPopup) {
          return props
            .confirmFn('Confirmation', {
              description: localization.getText('COMMON_MESSAGES_CONFIRM_SAVE')
            })
            .then(store.UpdateRelationshipToSolar.bind(store))
            .then(function(solarResponseResult) {
              if(solarResponseResult)
              {
                store.save();
              }
            });
        } else {
          return store.validateModifiedRelationships().then(handleAfterValidate);
        }
      }
      if (!success) {
        return store.showInvalidScreen ? store.validateModifiedRelationships().then(handleAfterValidate) : validation.validate();
      }
      return null;
    })
    .then(saveDone)
    .then(function(saveErrors) {
      if (!isEmpty(saveErrors)) {
        validation.updateErrors(saveErrors);
      }
      return saveErrors;
    })
    // Reload indicator for middle panel after save successfully
    .then(saveErrors => {
      isEmpty(saveErrors) && dataModelStore.loadIndicator();
    })
    .finally(loader.endLoading.bind(loader));
}

function onAdd(props: any, placeholder: any): void {
  const { validation, store } = props;

  return store.addNew().then(function() {
    validation.validate();
    return null;
  });
}

function onSearch(props: any, searchProperty: any, searchText: string): void {
  const { store, validation } = props;

  const refreshData = function() {
    props.validation.reset();
    // Update modified data before reseting
    store.syncModifiedRelationships();

    store.resetData();
    store.pageInfo.setSearchValues(searchProperty, searchText);
    props.updateLoading(true);
    props.loader.startLoading();

    return store.showInvalidScreen ?
      store
        .getInvalidRelationships()
        .then(props.updateLoading.bind(props, false))
        .finally(function() {
          validateInvalidRelationships(store, validation);
          props.loader.endLoading();
        }) :
      store
        .getRelationships()
        .then(props.updateLoading.bind(props, false))
        .finally(function() {
          props.loader.endLoading();
          validation.validate();
        });
  };

  if (store.showInvalidScreen) {
    return refreshData();
  }

  return props.confirmDirtyForm(store.hasChange).then(refreshData.bind(this));
}

function onCancel(props: any, placeholder: any): void {
  const { store, loader } = props;

  return props.confirmDirtyForm(store.hasChange).then(function() {
    if (props.isPopup) {
      props.onClosePopup();
    } else {
      store.setShowInvalidScreen(false);
      loader.startLoading();
      store.resetData();
      return store.getRelationships().finally(loader.endLoading.bind(loader));
    }
  });
}

export default actions;



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/DataModel/Relationship/Izenda.RelationshipActions.js